/**
 * Owns the pre-ranking phase of the staff create-form flow: fetches the template's
 * pre-ranking section, renders its questions in the scrolling ModalBody, and the
 * Back/Create actions in the pinned ModalFooter. A single Formik wraps both body
 * and footer, so the footer's Create reads submit/validity from context directly
 * (no imperative ref, no mirrored validity). Nothing is persisted here; captured
 * answers are handed back via `onSubmit` when the form is created.
 */
import {
  Box,
  Button,
  Center,
  Flex,
  ModalBody,
  ModalFooter,
  Spinner,
  VStack,
} from "@chakra-ui/react";
import { Formik, useFormikContext } from "formik";
import * as React from "react";
import { InfoAlert } from "src/components/Feedback/InfoAlert";
import { Glossary } from "src/components/Text/Glossary";
import { FormQuestion } from "src/scenes/parent/forms/components/Inputs/FormQuestion";
import { Answer } from "src/services/formTemplate";
import * as Question from "src/services/formTemplate/question";
import { usePreRankingSectionRemoteData } from "src/services/formTemplate/useFormTemplateSection";
import * as Visibility from "src/services/formTemplate/visibility";
import * as AFF from "src/services/formTemplateFilters";
import { validateFormTemplate } from "src/services/formTemplateValidations";
import * as AF from "src/types/formTemplate";

// A staff-created response starts with no ranked schools and no previous form.
const NO_SELECTED_SCHOOL_IDS = {
  rankedSchoolIds: [] as uuid[],
  previousFormSchoolIds: [] as uuid[],
};

type Props = {
  formTemplateId: uuid;
  applicant: AFF.Types.Applicant;
  // Rendered at the top of the body: the school whose response is being created.
  schoolSelect: React.ReactNode;
  schoolSelectedId: string;
  isBusy: boolean;
  notEligibleOpen: boolean;
  studentName: string;
  onBack: () => void;
  onCloseNotEligible: () => void;
  onDone: () => void;
  onSubmit: (
    answers: Answer.FormikValues,
    questions: readonly AF.Question<AF.WithId>[]
  ) => void | Promise<void>;
};

export function CreateFormPreRankingPhase(props: Props): React.ReactElement {
  const { formTemplateId, isBusy, onBack, onCloseNotEligible, onDone } = props;
  const sectionRD = usePreRankingSectionRemoteData(formTemplateId);

  // Before the section resolves (or on error) there is no form to submit, so show
  // the body status with a disabled Create; the footer stays consistent.
  if (sectionRD.hasError()) {
    return (
      <>
        <ModalBody as={VStack} padding={6} pb={4}>
          <InfoAlert status="error">
            Unable to load the form questions. Please try again.
          </InfoAlert>
        </ModalBody>
        <PreRankingFooter
          notEligibleOpen={false}
          isBusy={isBusy}
          createDisabled
          onBack={onBack}
          onCreate={() => {}}
          onCloseNotEligible={onCloseNotEligible}
          onDone={onDone}
        />
      </>
    );
  }

  if (!sectionRD.hasData()) {
    return (
      <>
        <ModalBody as={VStack} padding={6} pb={4}>
          <Center width="100%" paddingY={8}>
            <Spinner />
          </Center>
        </ModalBody>
        <PreRankingFooter
          notEligibleOpen={false}
          isBusy={isBusy}
          createDisabled
          onBack={onBack}
          onCreate={() => {}}
          onCloseNotEligible={onCloseNotEligible}
          onDone={onDone}
        />
      </>
    );
  }

  return <PreRankingForm {...props} section={sectionRD.data} />;
}

type FormProps = Props & { section: AF.PreRankingSection<AF.WithId> };

function PreRankingForm({
  section,
  applicant,
  schoolSelect,
  schoolSelectedId,
  isBusy,
  notEligibleOpen,
  studentName,
  onBack,
  onCloseNotEligible,
  onDone,
  onSubmit,
}: FormProps): React.ReactElement {
  const initialValues = React.useMemo(
    () => Answer.getFormikInitialValues(section.questions, [], [], [], [], []),
    [section.questions]
  );

  // No form exists yet, so we assemble the visibility context the same way
  // FormProvider does (see src/components/Providers/FormProvider.tsx), scoped to
  // this section and kept in sync with the answers captured so far.
  const [answers, setAnswers] =
    React.useState<Answer.FormikValues>(initialValues);
  const visibilityContext = React.useMemo(
    () => buildVisibilityContext(section.questions, answers),
    [section.questions, answers]
  );

  const applicableQuestions = React.useMemo(
    () =>
      Question.getCompleteApplicableQuestions(
        section.questions,
        answers,
        NO_SELECTED_SCHOOL_IDS,
        visibilityContext
      ),
    [section.questions, answers, visibilityContext]
  );

  // File uploads cannot be answered before the form exists, so a required one must
  // not block Create. Exclude them from validation (they stay rendered, disabled).
  const validationQuestions = React.useMemo(
    () => section.questions.filter((q) => q.type !== AF.FileUploadType),
    [section.questions]
  );

  const handleSubmit = React.useCallback(
    (values: Answer.FormikValues) => {
      // Recompute applicable questions from the submitted values so the caller
      // persists answers for exactly the questions that were shown.
      const submittedQuestions = Question.getCompleteApplicableQuestions(
        section.questions,
        values,
        NO_SELECTED_SCHOOL_IDS,
        buildVisibilityContext(section.questions, values)
      );
      // Return the promise so Formik awaits it and resets isSubmitting; otherwise
      // the Create button stays stuck loading after an error keeps the dialog open.
      return onSubmit(values, submittedQuestions);
    },
    [section.questions, onSubmit]
  );

  return (
    <Formik<Answer.FormikValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateFormTemplate(
        validationQuestions,
        NO_SELECTED_SCHOOL_IDS,
        visibilityContext
      )}
      validateOnBlur
    >
      {(formik) => (
        <>
          <ModalBody as={VStack} padding={6} pb={4}>
            <AnswersTracker onChange={setAnswers} />
            {notEligibleOpen && (
              <InfoAlert status="info">
                <Glossary>
                  {`${studentName} is not eligible for this school. Please review the previous answers, or contact the organization's administrator for help.`}
                </Glossary>
              </InfoAlert>
            )}
            {/* Keep the questions mounted while the not-eligible alert shows so
                returning to them does not wipe the captured answers. */}
            <Box width="100%" display={notEligibleOpen ? "none" : "block"}>
              <Flex direction="column" gap={4} width="100%">
                {schoolSelect}
                <Flex direction="column" gap={2}>
                  {applicableQuestions.map((question) => {
                    const isFileUpload = question.type === AF.FileUploadType;
                    return (
                      <FormQuestion
                        // No form row exists during creation, so there is no formId
                        // and answers live in Formik only. Address book and document
                        // library require a saved form and are disabled here.
                        formId=""
                        applicant={applicant}
                        key={question.id}
                        question={question}
                        marginBottom={4}
                        disableAddressBook
                        disableDocumentLibray
                        isDisabled={isBusy || isFileUpload}
                        disabledReason={
                          isFileUpload
                            ? "This question can only be answered after eligibility is verified and the form is created."
                            : undefined
                        }
                      />
                    );
                  })}
                </Flex>
              </Flex>
            </Box>
          </ModalBody>
          <PreRankingFooter
            notEligibleOpen={notEligibleOpen}
            isBusy={isBusy}
            createDisabled={!schoolSelectedId || !formik.isValid}
            onBack={onBack}
            onCreate={() => formik.submitForm()}
            onCloseNotEligible={onCloseNotEligible}
            onDone={onDone}
          />
        </>
      )}
    </Formik>
  );
}

type FooterProps = {
  notEligibleOpen: boolean;
  isBusy: boolean;
  createDisabled: boolean;
  onBack: () => void;
  onCreate: () => void;
  onCloseNotEligible: () => void;
  onDone: () => void;
};

function PreRankingFooter({
  notEligibleOpen,
  isBusy,
  createDisabled,
  onBack,
  onCreate,
  onCloseNotEligible,
  onDone,
}: FooterProps): React.ReactElement {
  return (
    <ModalFooter
      gap={2}
      pt={2}
      pb={4}
      borderTop="1px solid"
      borderColor="gray.200"
    >
      {notEligibleOpen ? (
        <>
          <Button
            variant="ghost"
            onClick={onCloseNotEligible}
            isDisabled={isBusy}
          >
            Back
          </Button>
          <Button type="button" onClick={onDone}>
            Done
          </Button>
        </>
      ) : (
        <>
          <Button variant="ghost" onClick={onBack} isDisabled={isBusy}>
            Back
          </Button>
          <Button
            type="button"
            onClick={onCreate}
            isLoading={isBusy}
            isDisabled={createDisabled}
          >
            Create
          </Button>
        </>
      )}
    </ModalFooter>
  );
}

/**
 * Keeps a copy of the current Formik answers in the parent so the visibility
 * context and applicable-question list can react to answer changes. Mirrors
 * FormProvider's FormWithAnswersTracker, which updates its answers map the same
 * way.
 */
function AnswersTracker({
  onChange,
}: {
  onChange: (answers: Answer.FormikValues) => void;
}): null {
  const { values } = useFormikContext<Answer.FormikValues>();
  React.useEffect(() => {
    onChange(values);
  }, [values, onChange]);
  return null;
}

/**
 * Assembles a VisibilityContext for a not-yet-created form, scoped to a single
 * section. Mirrors the maps FormProvider builds; a staff-created response has no
 * selected schools and no verified answers yet.
 */
function buildVisibilityContext(
  questions: AF.Question<AF.WithId>[],
  answers: Answer.FormikValues
): Visibility.VisibilityContext {
  const completeQuestions = Question.getCompleteQuestions(questions);
  const questionKeysMap = new Map<string, uuid>();
  const optionsMap = new Map<uuid, string>();
  for (const question of completeQuestions) {
    if (question.key) {
      questionKeysMap.set(question.key, question.id);
    }

    if (Question.hasOptions(question)) {
      for (const option of question.options) {
        if (question.type === AF.GradesType) {
          optionsMap.set(option.id, option.value ?? option.label);
        } else if (option.value) {
          optionsMap.set(option.id, option.value);
        }
      }
    }
  }

  return {
    sortedQuestionIds: completeQuestions.map((question) => question.id),
    visibilityMap: Visibility.toVisibilityMap([...completeQuestions]),
    formAnswersMap: new Map(Object.entries(answers)),
    questionKeysMap,
    optionsMap,
    selectedSchoolsSet: new Set<string>(),
    questionsWithVerifiedAnswers: new Set<uuid>(),
  };
}
